/**
 * The field families, as working controls rather than a written table.
 *
 * Every row here renders the REAL component. That is the whole design: a hand-authored legend is a
 * second source of truth, and this codebase has already paid for one of those — 47 copies of a chip
 * that had silently drifted apart on weight, padding and width. A swatch that is literally the
 * field it describes cannot go stale, and a grammar you can type into tells you more than a
 * sentence about the grammar does.
 *
 * The swatches follow the same rule. Each is an empty element carrying the family's own
 * `data-field-kind`, so it is painted by the same CSS as the fields it sits above. This panel
 * states no hex of its own and cannot disagree with the tokens.
 *
 * Lives over the block library, on the LEFT. The fields under inspection are in the editor panel on
 * the right, and a reference you have to look away from — another tab, the other side of the thing
 * you are comparing — is one you stop using.
 *
 * ENGLISH ONLY, deliberately, against the usual no-hard-coded-text rule. This describes component
 * APIs and grammars to whoever is building blocks; it is not teacher-facing, and filling pt-BR with
 * untranslated copies of "decimal mantissa plus an optional exponent" would pollute the locale
 * parity count with strings no translator should ever be handed. The two strings that DO appear in
 * teacher chrome — the toggle label in Quick settings and this panel's title — go through i18n.
 */
import { useState } from 'react';

import { FormulaField } from './formula-field';
import { UnitField } from './unit-field';

import { DateField } from '@/components/ui/date-field';
import { NumberField } from '@/components/ui/number-field';
import { SelectField } from '@/components/ui/select-field';
import { TextField } from '@/components/ui/text-field';

interface Family {
  kind: string;
  component: string;
  /** What it accepts, and what it silently refuses. The part you cannot see by looking. */
  accepts: string;
  /** Rendered with its own state so the row is a test bench, not a picture of one. */
  sample: (value: string, onChange: (v: string) => void, placeholder?: string) => React.ReactNode;
  /**
   * A suggestion, shown as the placeholder — never as the initial value. Seeding state with it
   * would bypass the grammar and open the panel with each field displaying text it claims to
   * refuse, which is worse than no example at all.
   */
  probe: string;
}

const GROUPS: { heading: string; note: string; families: Family[] }[] = [
  {
    heading: 'Rich surfaces',
    note: 'Own their own editing model. Both accept variable chips.',
    families: [
      {
        kind: 'richtext', component: 'RichTextEditor',
        accepts: 'Formatted text, variable chips, math and chem nodes. Multi-line. No error state of its own.',
        probe: '',
        // Swatch only: Tiptap mounts a full editor per instance, and standing one up every time this
        // panel opens costs more than the row is worth. The swatch still carries the real tint.
        sample: () => null,
      },
      {
        kind: 'formula', component: 'FormulaField',
        accepts: 'A mathjs expression, evaluated live. TWO SHAPES: with variables on the task it is a chip surface with a picker; with none it is a plain input with the numeric keypad and no picker. Same family either way — whether a variable can go in is a fact about the task, not about the field. `100*pi` is 314.159 in both.',
        probe: '2*(3+4)',
        sample: (v, on, ph) => <FormulaField value={v} onChange={on} variables={[]} rolledValues={{}} placeholder={ph} ariaLabel='formula sample' />,
      },
    ],
  },
  {
    heading: 'Text',
    note: 'Nothing is stripped. The variant decides the face and the meaning. For a field that also accepts mathjs, see FormulaField above — that is not a text variant.',
    families: [
      {
        kind: 'text-prose', component: "TextField variant='prose'",
        accepts: 'Anything. Text ABOUT content: alt text, a transcript, a name, an axis label.',
        probe: 'Sample name',
        sample: (v, on, ph) => <TextField variant='prose' value={v} onChange={on} placeholder={ph} ariaLabel='text-prose sample' />,
      },
      {
        kind: 'text-prose', component: "TextField variant='prose' rows={3}",
        accepts: 'The same family in its multiline shape — alt text, a transcript, a caption. A SHAPE, not a fourth variant: how much you see at once is a property of the content, not of the kind of text it is.',
        probe: '',
        sample: (v, on, ph) => <TextField variant='prose' rows={3} value={v} onChange={on} placeholder={ph} ariaLabel='multiline prose sample' />,
      },
      {
        kind: 'text-literal', component: "TextField variant='literal'",
        accepts: 'Anything, MONOSPACED. Content a student is matched against, so whitespace and lookalike characters show.',
        probe: 'l1 vs I1',
        sample: (v, on, ph) => <TextField variant='literal' value={v} onChange={on} placeholder={ph} ariaLabel='text-literal sample' />,
      },
      {
        kind: 'unit', component: 'UnitField',
        accepts: 'A unit string, parsed by mathjs — `kg`, `m/s`, `mg·L⁻¹`. NOT a rich surface: no '
          + 'chips, no formatting. Typing suggests units, matching the token under the caret so a '
          + 'compound can be built a part at a time. The picker carries the characters a keyboard '
          + 'cannot reach (µ ° Ω Å ² ³ ⁻¹ · ‰ ⊙ ⊕ ♃); everything it inserts is normalized to the '
          + 'ASCII form mathjs wants. Under `strict` (the student\'s box) only a unit that parses '
          + 'may leave the field — anything else is discarded on blur.',
        probe: 'mg·L⁻¹',
        sample: (v, on) => <UnitField value={v} onChange={on} ariaLabel='unit sample' />,
      },
    ],
  },
  {
    heading: 'Numbers',
    note: 'Filtered as you type. Both . and , are accepted; the value leaves as one canonical dot.',
    families: [
      {
        kind: 'integer', component: "NumberField grammar='integer'",
        accepts: 'Digits only, plus a leading minus when allowNegative is set.',
        probe: '12.5abc',
        sample: (v, on, ph) => <NumberField grammar='integer' value={v} onChange={on} placeholder={ph} ariaLabel='integer sample' />,
      },
      {
        kind: 'decimal', component: "NumberField grammar='decimal'",
        accepts: 'Digits and EXACTLY ONE separator. Try 1.2.3 — the second dot is refused, where the old filterNumber let it through and parseFloat silently read 1.2.',
        probe: '1.2.3',
        sample: (v, on, ph) => <NumberField grammar='decimal' value={v} onChange={on} allowNegative placeholder={ph} ariaLabel='decimal sample' />,
      },
      {
        kind: 'scientific', component: "NumberField grammar='scientific'",
        accepts: 'A decimal mantissa plus an optional e/E exponent, which keeps its own sign.',
        probe: '6.02e-23',
        sample: (v, on, ph) => <NumberField grammar='scientific' value={v} onChange={on} allowNegative placeholder={ph} ariaLabel='scientific sample' />,
      },
    ],
  },
  {
    heading: 'Pickers',
    note: 'UNTINTED on purpose. A chevron and a calendar announce themselves, and neither has a grammar to get wrong, so there is no wrong family to catch. They keep their data-field-kind for the coverage sweep.',
    families: [
      {
        kind: 'select', component: 'SelectField',
        accepts: 'One of a fixed list. Errors show immediately: there is no half-finished state to wait out.',
        probe: '',
        sample: (v, on) => (
          <SelectField value={v || 'a'} onChange={on} ariaLabel='select sample'
            options={[{ value: 'a', label: 'First' }, { value: 'b', label: 'Second' }]} />
        ),
      },
      {
        kind: 'date', component: 'DateField',
        accepts: 'A yyyy-mm-dd value from the native calendar. Immediate errors, for the same reason.',
        probe: '',
        sample: (v, on) => <DateField value={v} onChange={on} ariaLabel='date sample' />,
      },
    ],
  },
];

function FamilyRow({ family }: { family: Family }) {
  const [value, setValue] = useState('');
  return (
    <div className='flex flex-col gap-1.5 px-3 py-3'>
      <div className='flex items-center gap-2'>
        {/* The swatch IS the tint: an empty element carrying the family's own attribute, so it
            cannot disagree with the fields below it. */}
        <span data-field-kind={family.kind}
          className='h-4 w-4 shrink-0 rounded border border-ws-border' />
        <code className='text-xs font-semibold text-ws-text'>{family.kind}</code>
      </div>
      <code className='text-[11px] text-ws-muted'>{family.component}</code>
      {family.sample(value, setValue, family.probe ? `try ${family.probe}` : undefined)}
      <p className='text-[11px] leading-snug text-ws-muted'>{family.accepts}</p>
    </div>
  );
}

export default function FieldReference() {
  return (
    // `data-field-audit='on'` scoped to this panel, so the reference always shows its colours even
    // with the global switch off — you consult it to decide whether to turn the switch on.
    <div data-field-audit='on' className='flex-1 overflow-y-auto'>
      <p className='px-3 pt-3 text-[11px] leading-snug text-ws-muted'>
        Every control below is the real component. Type in them: the grammar you see is the grammar
        the blocks use. A field in the editor with NO tint is a raw input that was never migrated.
      </p>
      {GROUPS.map(({ heading, note, families }) => (
        <div key={heading} className='mt-2 border-t border-ws-border pt-1'>
          <p className='px-3 pt-2 text-base font-semibold text-ws-text'>{heading}</p>
          <p className='px-3 pb-1 text-[11px] leading-snug text-ws-muted'>{note}</p>
          {families.map(f => <FamilyRow key={f.component} family={f} />)}
        </div>
      ))}
    </div>
  );
}
